import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, ConfigProvider, Space, Typography, theme } from 'antd';
import { message } from '../services/toastMessage';
import { motion } from 'framer-motion';
import { IconGripVertical, IconX, IconClock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import dayjs, { Dayjs } from 'dayjs';
import { IDayAvailability } from '../interfaces/heavyDutyMachinery/IAvailability';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatFullDate } from '../helpers/dateFormatHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import { TEAL, TEAL_SOFT_BG, SURFACE_1_SOFT, BOOKED_STRIPES, BUFFER_STRIPES, TOO_SOON_STRIPES } from '../theme/creamyTokens';
import { tealAccentTheme } from '../theme/appTheme';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';

const { Text } = Typography;

const MINUTES_PER_DAY = 24 * 60;
// The bar now spans the selected day PLUS the next one (see
// AvailabilityCalendar's `timelineDay`, which already concatenates both
// days' windows) so a guest can drag a selection straight across midnight
// (e.g. 23:30 -> 01:30) instead of hitting a hard 24h wall -- this is the
// one clamp that needs to grow to make room for that; everything else here
// (minutesSinceMidnight, hour-tick formatting) already works unmodified
// past minute 1440 since it's all plain arithmetic relative to dayStart.
const MINUTES_TOTAL = MINUTES_PER_DAY * 2;
const ENVELOPE_PADDING_MINUTES = 120;
// Fallback only -- used when a caller doesn't pass slotGranularityMinutes at
// all (e.g. an older/unrelated embed of this component). Callers that do
// pass it get the host's real "start every N minutes" setting instead, with
// 0 meaning no grid (see HeavyDutyMachinery.SlotGranularityMinutes's own doc
// comment) snapped to the finest practical step of 1 minute.
const SNAP_MINUTES = 5;
const HANDLE_SIZE = 22;
// The visible circle stays HANDLE_SIZE so the timeline keeps its light,
// creamy look -- a 44px disc here would dominate the 56px-tall bar. The
// *tappable* area is grown separately to the 44px minimum via an invisible
// wrapper (see DragHandle) instead of the circle itself.
const HANDLE_HIT_SIZE = 44;
// Mobile-only: instead of squeezing the bar into the ~340-380px viewport,
// it gets a fixed width wider than the screen and scrolls horizontally like
// a carousel -- doubled alongside MINUTES_TOTAL to keep the same ~37px/h
// density now that the bar covers 48h instead of 24h, see the PR
// description for how to tune it after real-device testing.
const MOBILE_BAR_WIDTH = 1800;
const AUTOSCROLL_EDGE_PX = 32;
const AUTOSCROLL_STEP_PX = 12;

type SegmentKind = 'free' | 'closed' | 'buffer' | 'booked' | 'tooSoon';

interface Segment {
  start: number; // minutes since midnight
  end: number;
  kind: SegmentKind;
}

interface MinuteRange {
  start: number;
  end: number;
}

interface HourlyRangeTimelineProps {
  day: IDayAvailability;
  date: Dayjs;
  bufferMinutes: number;
  minDurationMinutes: number;
  maxDurationMinutes?: number;
  // Both optional so an older/unrelated caller that doesn't pass them keeps
  // this component's previous behaviour exactly (5-minute snap, no
  // too-soon graying) instead of silently changing under it.
  minAdvanceBookingHours?: number;
  slotGranularityMinutes?: number;
  pricePerHour?: number;
  // ISO 4217 currency code for formatting pricePerHour/the live estimate
  // below -- omitted by a caller with no specific space yet (see
  // AvailabilityCalendar's own spaceId), in which case formatCurrency's own
  // fallback (see currencyHelper) is used.
  currencyCode?: string;
  onDraftChange: () => void;
  onConfirm: (range: { startAt: string; endAt: string }) => void;
  // Taller on a caller with a dedicated screen to itself (the mobile search
  // bar's own full-screen "When" picker) where dragging precision matters
  // more than compactness -- defaults to the original embedded-widget size.
  barHeight?: number;
}

/** Softened drag handle -- same grip icon as before, just a white/teal
 * circle with a subtle shadow instead of a solid blue disc, plus a slight
 * scale on hover/active for feedback. Holds no drag state itself: pointerdown
 * is forwarded straight to the caller's handler.
 *
 * The visible circle (HANDLE_SIZE, 22px) is wrapped in a larger, invisible
 * HANDLE_HIT_SIZE (44px) box that's centered on the same point -- the actual
 * tap/drag target is the outer box, so the circle can stay small without the
 * handle being uncomfortably fiddly to grab on a phone. */
const DragHandle: React.FC<{
  side: 'start' | 'end';
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
}> = ({ side, onPointerDown }) => {
  const [active, setActive] = useState(false);
  return (
    <div
      onPointerDown={(e) => {
        setActive(true);
        onPointerDown(e);
      }}
      onPointerUp={() => setActive(false)}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      style={{
        position: 'absolute',
        [side === 'start' ? 'left' : 'right']: -HANDLE_HIT_SIZE / 2,
        top: '50%',
        transform: 'translateY(-50%)',
        width: HANDLE_HIT_SIZE,
        height: HANDLE_HIT_SIZE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'ew-resize',
        touchAction: 'none',
      }}
    >
      <div
        style={{
          transform: `scale(${active ? 1.12 : 1})`,
          transition: 'transform 150ms ease',
          width: HANDLE_SIZE,
          height: HANDLE_SIZE,
          borderRadius: '50%',
          background: '#fff',
          color: TEAL,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 1px 4px rgba(0,0,0,0.15), 0 0 0 2px ${TEAL}`,
        }}
      >
        <IconGripVertical size={12} />
      </div>
    </div>
  );
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const minutesSinceMidnight = (iso: string, dayStart: Dayjs) => dayjs(iso).diff(dayStart, 'minute');

// Deliberately NOT .toISOString() -- that converts through the BROWSER's own
// timezone to a real UTC instant, which silently corrupts the picked slot
// whenever the guest isn't in the same zone as the space itself (the actual
// bug this was fixing: a guest's local afternoon could land on the wrong
// side of the host's schedule/an existing booking once "corrected" to UTC).
// day.openWindows/busyWindows above are already offset-less strings the
// backend means as the space's own local wall clock (see
// AvailabilityCalculator's doc comment) -- dayjs parses those the same
// literal way it parses this plain format back out, so sending this instead
// keeps every number exactly what was drawn on the bar, with no real
// timezone math anywhere in this component at all.
const toSpaceLocalIso = (d: Dayjs) => d.format('YYYY-MM-DDTHH:mm:ss');

const formatClock = (minutes: number) => {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const formatDuration = (totalMinutes: number, t: TFunction): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) return `${hours} ${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_HOURS_SUFFIX)}`;
  if (hours === 0) return `${minutes} ${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DURATION_MINUTES_SUFFIX)}`;
  return `${hours}h ${minutes}${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DURATION_MINUTES_SUFFIX)}`;
};

// Paints [start,end) with `kind` over the existing segment list, splitting
// any segment it partially overlaps -- later paints win over earlier ones,
// which is how open windows (painted first) end up covered by buffer/booked
// windows (painted after) without needing a separate merge step.
const paint = (segments: Segment[], start: number, end: number, kind: SegmentKind): Segment[] => {
  if (start >= end) return segments;
  const next: Segment[] = [];
  segments.forEach((segment) => {
    if (segment.end <= start || segment.start >= end) {
      next.push(segment);
      return;
    }
    if (segment.start < start) next.push({ start: segment.start, end: start, kind: segment.kind });
    next.push({ start: Math.max(segment.start, start), end: Math.min(segment.end, end), kind });
    if (segment.end > end) next.push({ start: end, end: segment.end, kind: segment.kind });
  });
  return next.filter((segment) => segment.start < segment.end);
};

const findFreeRun = (segments: Segment[], point: number): MinuteRange | undefined => {
  const segment = segments.find((s) => point >= s.start && point <= s.end && s.kind === 'free');
  return segment ? { start: segment.start, end: segment.end } : undefined;
};

/**
 * Calendly/Turo-style free-drag timeline for the "Por horas" rental mode --
 * replaces the old fixed 30-minute slot buttons. Reads the same
 * OpenWindows/BusyWindows the batched availability endpoint already
 * computes; the only client-side math is reconstructing, from each busy
 * window and the space's BufferMinutes, which part is the actual booking
 * (solid) vs. the cleaning buffer around it (striped) -- the backend only
 * ever hands over the merged [start-buffer, end+buffer] window.
 *
 * On mobile the bar becomes a horizontally-scrollable, fixed-width carousel
 * (see MOBILE_BAR_WIDTH) instead of squeezing 24h into the viewport, and drag
 * releases auto-commit the selection (see rangeRef/handlePointerUp below) so
 * the inline "Continuar" button -- otherwise redundant with an outer confirm
 * button on every mobile caller -- can be hidden there. Desktop/tablet keep
 * the original compact bar and manual two-step confirm untouched.
 */
const HourlyRangeTimeline: React.FC<HourlyRangeTimelineProps> = ({
  day,
  date,
  bufferMinutes,
  minDurationMinutes,
  maxDurationMinutes,
  minAdvanceBookingHours,
  slotGranularityMinutes,
  pricePerHour,
  currencyCode,
  onDraftChange,
  onConfirm,
  barHeight = 56,
}) => {
  const { token } = theme.useToken();
  const { t, i18n } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const barRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<
    | { handle: 'start'; run: MinuteRange }
    | { handle: 'end'; run: MinuteRange }
    | { handle: 'move'; run: MinuteRange; anchor: number; original: MinuteRange }
    | undefined
  >(undefined);
  // Mirrors `range` synchronously (setState is batched/async) so drag-release
  // auto-confirm and the autoscroll interval tick -- both of which need the
  // just-updated value in the same tick they set it -- never read a stale one.
  const rangeRef = useRef<MinuteRange | undefined>(undefined);
  const lastPointerXRef = useRef(0);
  const autoScrollIntervalRef = useRef<number | undefined>(undefined);

  const [range, setRange] = useState<MinuteRange | undefined>(undefined);
  const [confirmed, setConfirmed] = useState(false);
  const [scrollAtEnd, setScrollAtEnd] = useState(false);
  // A one-time "drag like this" coach mark on mobile -- guests weren't
  // noticing the bar was interactive at all (see the contrast fix above),
  // and a static bar with no selection yet gives no other clue that it's
  // draggable. Plays once per newly-picked day (reset alongside range/
  // confirmed below) and disappears the moment either the demo finishes or
  // the guest starts a real selection, whichever comes first.
  const [showGestureHint, setShowGestureHint] = useState(true);

  const updateRange = (next: MinuteRange | undefined) => {
    rangeRef.current = next;
    setRange(next);
  };

  const dayStart = useMemo(() => date.startOf('day'), [date]);

  // 0 means the host set no start-time grid at all -- snap to the finest
  // practical step (1 minute) instead, so a guest really can start the
  // instant a slot frees up. undefined (prop not passed) keeps the old
  // fixed 5-minute snap untouched.
  const snapMinutes = slotGranularityMinutes == null ? SNAP_MINUTES : Math.max(1, slotGranularityMinutes);
  const snap = (minutes: number) => Math.round(minutes / snapMinutes) * snapMinutes;

  const segments = useMemo<Segment[]>(() => {
    const openStarts = day.openWindows.map((w) => minutesSinceMidnight(w.start, dayStart));
    const openEnds = day.openWindows.map((w) => minutesSinceMidnight(w.end, dayStart));
    const envelopeStart = Math.min(...openStarts);
    const envelopeEnd = Math.max(...openEnds);
    const barStart = Math.max(0, Math.floor((envelopeStart - ENVELOPE_PADDING_MINUTES) / 60) * 60);
    const barEnd = Math.min(MINUTES_TOTAL, Math.ceil((envelopeEnd + ENVELOPE_PADDING_MINUTES) / 60) * 60);

    let segs: Segment[] = [{ start: barStart, end: barEnd, kind: 'closed' }];
    day.openWindows.forEach((w) => {
      segs = paint(segs, minutesSinceMidnight(w.start, dayStart), minutesSinceMidnight(w.end, dayStart), 'free');
    });
    // "Now" (plus the host's minAdvanceBookingHours, 0 if unset) painted over
    // whatever's free before it -- covers both the minimum-notice rule and
    // the more basic "you can't book a time that's already passed today"
    // case, which the backend's own OpenWindows never encoded either.
    // Painted before buffer/booked below so an actual booking still wins
    // where the two overlap, rather than this graying over a real booking.
    const earliestBookableMinutes = dayjs().add(minAdvanceBookingHours ?? 0, 'hour').diff(dayStart, 'minute');
    segs = paint(segs, barStart, earliestBookableMinutes, 'tooSoon');
    // day.busyWindows may arrive as two separate fragments of the very same
    // booking when it crosses midnight: AvailabilityCalculator.GetDayAvailability
    // clips each booking's [start, end + buffer) window to the single
    // calendar day it's asked about, so a booking that ends after midnight
    // produces one fragment ending exactly at that day's midnight and
    // another starting exactly there -- concatenating today's and
    // tomorrow's busyWindows (see AvailabilityCalendar's timelineDay) puts
    // both fragments in this list. Merging touching/overlapping ones back
    // into a single window BEFORE subtracting bufferMinutes below is what
    // keeps a single trailing buffer at the real end, instead of the
    // artificial midnight seam also being mistaken for the booking's end
    // and growing its own spurious buffer stripe there.
    const busyRuns: MinuteRange[] = day.busyWindows
      .map((w) => ({ start: minutesSinceMidnight(w.start, dayStart), end: minutesSinceMidnight(w.end, dayStart) }))
      .sort((a, b) => a.start - b.start)
      .reduce<MinuteRange[]>((acc, w) => {
        const prev = acc[acc.length - 1];
        if (prev && w.start <= prev.end) {
          prev.end = Math.max(prev.end, w.end);
        } else {
          acc.push({ ...w });
        }
        return acc;
      }, []);
    // Only the trailing bufferMinutes gets its own 'buffer' segment here --
    // rawStart is rendered as 'booked' outright. (Note: the backend's own
    // busyWindow envelope is actually [start - buffer, end + buffer], so a
    // leading buffer exists too but currently just reads as part of
    // 'booked' rather than its own striped segment -- a separate, narrower
    // cosmetic gap unrelated to the midnight-seam bug this merge fixes.)
    busyRuns.forEach(({ start: rawStart, end: rawEnd }) => {
      const coreEnd = rawEnd - bufferMinutes;
      if (rawStart < coreEnd) {
        segs = paint(segs, rawStart, coreEnd, 'booked');
        segs = paint(segs, coreEnd, rawEnd, 'buffer');
      } else {
        segs = paint(segs, rawStart, rawEnd, 'booked');
      }
    });
    segs = segs.sort((a, b) => a.start - b.start);
    // Merge touching same-kind segments -- without this, two independently
    // painted 'free' windows that happen to be exactly adjacent (e.g. today's
    // open hours ending right where tomorrow's begin, now that the bar spans
    // both days) stay as two separate Segment objects. findFreeRun only ever
    // returns the single segment under the drag point, so a drag started on
    // one side would get clamped right at that boundary even though the
    // underlying availability is genuinely continuous across it.
    const merged: Segment[] = [];
    segs.forEach((segment) => {
      const prev = merged[merged.length - 1];
      if (prev && prev.kind === segment.kind && prev.end === segment.start) {
        prev.end = segment.end;
      } else {
        merged.push({ ...segment });
      }
    });
    return merged;
  }, [day, dayStart, bufferMinutes, minAdvanceBookingHours]);

  const barStart = segments[0]?.start ?? 0;
  const barEnd = segments[segments.length - 1]?.end ?? MINUTES_TOTAL;
  const barSpan = barEnd - barStart;

  // Where the selected day ends and the next one begins, in the same
  // minutes-since-dayStart space as everything else here -- drawn as a
  // subtle divider plus the next day's date so a 48h-wide bar still reads
  // unambiguously as "two different days", not one very long one.
  const dayBoundaryMinutes = MINUTES_PER_DAY;
  const showDayBoundary = dayBoundaryMinutes > barStart && dayBoundaryMinutes < barEnd;

  // Large, semi-transparent date watermark centered within each day's own
  // portion of the (now up-to-48h-wide) bar -- purely decorative context for
  // "which day am I looking at/dragging into", never a hit target (see
  // pointerEvents: 'none' below): the guest must always be able to select
  // any hour underneath it. Skipped for a day whose visible sliver is too
  // thin to hold a legible label (e.g. the bar barely pokes past midnight).
  const dayWatermarks = useMemo(() => {
    const marks: { key: string; label: string; center: number }[] = [];
    const MIN_SLIVER_MINUTES = 90;
    const day1Start = barStart;
    const day1End = Math.min(barEnd, dayBoundaryMinutes);
    if (day1End - day1Start >= MIN_SLIVER_MINUTES) {
      marks.push({ key: 'day1', label: dayStart.format('D MMM'), center: (day1Start + day1End) / 2 });
    }
    const day2Start = Math.max(barStart, dayBoundaryMinutes);
    const day2End = barEnd;
    if (day2End - day2Start >= MIN_SLIVER_MINUTES) {
      marks.push({ key: 'day2', label: dayStart.add(1, 'day').format('D MMM'), center: (day2Start + day2End) / 2 });
    }
    return marks;
  }, [barStart, barEnd, dayBoundaryMinutes, dayStart]);

  // Any previously confirmed selection stops being valid the moment the
  // guest starts adjusting the drag range again -- they must hit "Continuar"
  // again before the parent (waiver + submit) reacts to the new range.
  useEffect(() => {
    updateRange(undefined);
    setConfirmed(false);
    setShowGestureHint(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current != null) {
      window.clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = undefined;
    }
  };

  useEffect(() => stopAutoScroll, []);

  const pointToMinutes = (clientX: number): number => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return barStart;
    const pct = clamp((clientX - rect.left) / rect.width, 0, 1);
    return snap(barStart + pct * barSpan);
  };

  const invalidateConfirmation = () => {
    if (confirmed) {
      setConfirmed(false);
      onDraftChange();
    }
  };

  const startNewSelection = (point: number): boolean => {
    const run = findFreeRun(segments, point);
    if (!run) return false;
    const end = Math.min(point + minDurationMinutes, run.end);
    const start = Math.max(run.start, end - minDurationMinutes);
    updateRange({ start, end });
    invalidateConfirmation();
    return true;
  };

  // A genuine tap (no scroll) on empty bar area starts a fresh selection
  // there, replacing any existing one. Deliberately bound to `onClick` rather
  // than `onPointerDown`: on mobile the bar sits inside a horizontally
  // scrollable carousel (see MOBILE_BAR_WIDTH below), and click is the one
  // event browsers already suppress when the same gesture turned into a
  // scroll -- which is exactly the tap-vs-swipe distinction this needs, for
  // free, with no custom threshold math. The selection box and handles stop
  // propagation before this fires, so it only ever fires for taps that land
  // outside them.
  const handleBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const created = startNewSelection(pointToMinutes(e.clientX));
    if (isMobile && created) handleConfirm();
  };

  const handleHandlePointerDown = (which: 'start' | 'end') => (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!range) return;
    const anchor = which === 'start' ? range.end : range.start;
    const run = findFreeRun(segments, anchor);
    if (!run) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    dragStateRef.current = { handle: which, run };
  };

  // Dragging the selection itself (rather than a handle) keeps its duration
  // fixed and just slides it to a new spot -- the only way to reach a later
  // or earlier slot without resetting first. Bounded to the free run the
  // selection started in, same as the handles.
  const handleSelectionPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!range) return;
    const run = findFreeRun(segments, range.start);
    if (!run) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    dragStateRef.current = { handle: 'move', run, anchor: pointToMinutes(e.clientX), original: { ...range } };
  };

  const handleReset = () => {
    updateRange(undefined);
    invalidateConfirmation();
  };

  // Shared by both real pointermove events and the autoscroll interval tick
  // (which replays the last known clientX against the bar's new position
  // after it scrolls under a finger that isn't moving) -- reads/writes
  // rangeRef directly since the interval tick runs outside React's normal
  // render/closure cycle.
  const applyDragAtClientX = (clientX: number) => {
    const drag = dragStateRef.current;
    const current = rangeRef.current;
    if (!drag || !current) return;
    const point = pointToMinutes(clientX);
    if (drag.handle === 'start') {
      const minStart = maxDurationMinutes != null ? Math.max(drag.run.start, current.end - maxDurationMinutes) : drag.run.start;
      const start = clamp(point, minStart, current.end - minDurationMinutes);
      updateRange({ start, end: current.end });
    } else if (drag.handle === 'end') {
      const maxEnd = maxDurationMinutes != null ? Math.min(drag.run.end, current.start + maxDurationMinutes) : drag.run.end;
      const end = clamp(point, current.start + minDurationMinutes, maxEnd);
      updateRange({ start: current.start, end });
    } else {
      const duration = drag.original.end - drag.original.start;
      const start = clamp(drag.original.start + (point - drag.anchor), drag.run.start, drag.run.end - duration);
      updateRange({ start, end: start + duration });
    }
    invalidateConfirmation();
  };

  // While dragging a handle near the visible edge of the mobile carousel,
  // auto-scroll the container so the selection can extend beyond what's
  // currently visible without the guest having to lift their finger --
  // the standard calendar range-picker pattern.
  const maybeAutoScroll = (clientX: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    let direction = 0;
    if (clientX - rect.left < AUTOSCROLL_EDGE_PX && container.scrollLeft > 0) direction = -1;
    else if (rect.right - clientX < AUTOSCROLL_EDGE_PX && container.scrollLeft < container.scrollWidth - container.clientWidth) direction = 1;

    if (direction === 0) {
      stopAutoScroll();
      return;
    }
    if (autoScrollIntervalRef.current != null) return;
    autoScrollIntervalRef.current = window.setInterval(() => {
      const el = scrollRef.current;
      if (!el) return;
      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth;
      if ((direction < 0 && atStart) || (direction > 0 && atEnd)) {
        stopAutoScroll();
        return;
      }
      el.scrollLeft += direction * AUTOSCROLL_STEP_PX;
      applyDragAtClientX(lastPointerXRef.current);
    }, 16);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current) return;
    lastPointerXRef.current = e.clientX;
    applyDragAtClientX(e.clientX);
    if (isMobile) maybeAutoScroll(e.clientX);
  };

  const handlePointerUp = () => {
    const wasDragging = dragStateRef.current !== undefined;
    dragStateRef.current = undefined;
    stopAutoScroll();
    // Mobile has no inline "Continuar" button (see the footer below) -- a
    // drag release there IS the confirm action, reusing the same validation
    // handleConfirm already does for desktop's explicit button.
    if (isMobile && wasDragging && rangeRef.current) {
      handleConfirm();
    }
  };

  const handleConfirm = (targetRange: MinuteRange | undefined = rangeRef.current) => {
    if (!targetRange) return;
    const duration = targetRange.end - targetRange.start;
    // The drag handles are already physically clamped to [min, max] above --
    // this is a defensive re-check for whatever might still slip through
    // (e.g. a stale range left over from a prop change), matching the same
    // bounds BookingController re-validates server-side.
    if (duration < minDurationMinutes) {
      message.error(`${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DURATION_TOO_SHORT_ERROR)} ${formatDuration(minDurationMinutes, t)}`);
      return;
    }
    if (maxDurationMinutes != null && duration > maxDurationMinutes) {
      message.error(`${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DURATION_TOO_LONG_ERROR)} ${formatDuration(maxDurationMinutes, t)}`);
      return;
    }
    setConfirmed(true);
    onConfirm({
      startAt: toSpaceLocalIso(dayStart.add(targetRange.start, 'minute')),
      endAt: toSpaceLocalIso(dayStart.add(targetRange.end, 'minute')),
    });
  };

  const hourMarks = useMemo(() => {
    const marks: number[] = [];
    const firstMark = Math.ceil(barStart / 180) * 180;
    for (let m = firstMark; m <= barEnd; m += 180) marks.push(m);
    return marks;
  }, [barStart, barEnd]);

  // 'closed' (outside opening hours) intentionally renders as plain flat
  // gray -- only buffer/booked get a stripe, so "nothing to see here" reads
  // as exactly that, distinct from either kind of blocked time.
  const segmentStyle = (kind: SegmentKind): React.CSSProperties => {
    switch (kind) {
      case 'booked':
        return { background: BOOKED_STRIPES };
      case 'buffer':
        return { background: BUFFER_STRIPES };
      case 'tooSoon':
        return { background: TOO_SOON_STRIPES };
      case 'closed':
        return { background: token.colorFillQuaternary };
      // 'free' -- a warm cream tint rather than plain white: on a white
      // page background, a pure-white bar with no fill at rest was
      // reported as reading as empty page space rather than a control,
      // to the point guests didn't realize it was there to drag at all.
      default:
        return { background: SURFACE_1_SOFT };
    }
  };

  const durationMinutes = range ? range.end - range.start : 0;
  const durationLabel = useMemo(() => (range ? formatDuration(durationMinutes, t) : ''), [range, durationMinutes, t]);

  const price = pricePerHour != null && range ? (durationMinutes / 60) * pricePerHour : undefined;

  // A selection that crosses midnight reads as ambiguous with clock-only
  // times ("23:30 -> 01:30" doesn't say which day the 01:30 lands on) -- the
  // mobile summary spells out the full weekday+date on the end side
  // whenever that's the case (the start side's day is already established
  // by the date heading above this component), keeping the compact
  // clock-only format on both ends otherwise.
  const rangeStartDate = useMemo(() => dayStart.add(range?.start ?? 0, 'minute'), [dayStart, range?.start]);
  const rangeEndDate = useMemo(() => dayStart.add(range?.end ?? 0, 'minute'), [dayStart, range?.end]);
  const rangeSpansDays = !!range && !rangeStartDate.isSame(rangeEndDate, 'day');

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 2);
  };

  // Whether the mobile bar is actually wider than its viewport right now --
  // the edge-fade hint has nothing to hint at once it isn't (e.g. a very
  // wide phone), so it's suppressed instead of showing a fade to nowhere.
  useEffect(() => {
    if (isMobile) handleScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, day, barHeight]);

  return (
    // Fades + slides in on every mount -- this component is remounted
    // whenever the picked day changes (see AvailabilityCalendar's own
    // `key={selectedDate...}` on its <HourlyRangeTimeline>), including the
    // very first time a guest picks a day at all, which is exactly the
    // moment this needs to read as "something just appeared here" rather
    // than "this was already on the page and I somehow missed it".
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut' }}>
      {isMobile && range && (
        <Card size="small" bordered={false} style={{ borderRadius: 18, background: SURFACE_1_SOFT, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: '#fff',
                color: TEAL,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <IconClock size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              {rangeSpansDays ? (
                <Text strong style={{ fontSize: 16, display: 'block', color: TEAL, lineHeight: 1.4 }}>
                  {formatClock(range.start)} → {formatFullDate(rangeEndDate, i18n.language)}, {formatClock(range.end)}
                </Text>
              ) : (
                <Text strong style={{ fontSize: 18, display: 'block', color: TEAL }}>
                  {formatClock(range.start)} → {formatClock(range.end)}
                </Text>
              )}
              <Text type="secondary" style={{ fontSize: 13 }}>
                {durationLabel}
                {price != null && <> — {formatCurrency(price, currencyCode ?? '', i18n.language)}</>}
              </Text>
            </div>
          </div>
        </Card>
      )}

      <Text type="secondary" style={{ fontSize: 13 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_TIMELINE_DRAG_HINT)}
      </Text>

      <div
        ref={scrollRef}
        onScroll={isMobile ? handleScroll : undefined}
        className={isMobile ? 'no-scrollbar' : undefined}
        style={{
          position: 'relative',
          marginTop: 12,
          overflowX: isMobile ? 'auto' : 'visible',
          WebkitOverflowScrolling: 'touch',
          touchAction: isMobile ? 'pan-x' : undefined,
        }}
      >
        <div style={{ width: isMobile ? MOBILE_BAR_WIDTH : '100%' }}>
          <div
            ref={barRef}
            onClick={handleBarClick}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
              position: 'relative',
              height: barHeight,
              borderRadius: 18,
              overflow: 'hidden',
              cursor: 'pointer',
              userSelect: 'none',
              // A visible edge + lift even where every segment is 'free' --
              // without this the bar had no boundary of its own and read as
              // plain page background rather than a distinct control.
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            {segments.map((segment, index) => (
              <div
                key={index}
                style={{
                  position: 'absolute',
                  left: `${((segment.start - barStart) / barSpan) * 100}%`,
                  width: `${((segment.end - segment.start) / barSpan) * 100}%`,
                  top: 0,
                  bottom: 0,
                  ...segmentStyle(segment.kind),
                }}
              />
            ))}

            {showDayBoundary && (
              <div
                style={{
                  position: 'absolute',
                  left: `${((dayBoundaryMinutes - barStart) / barSpan) * 100}%`,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  background: 'rgba(0,0,0,0.18)',
                  pointerEvents: 'none',
                }}
              />
            )}

            {dayWatermarks.map((wm) => (
              <div
                key={wm.key}
                style={{
                  position: 'absolute',
                  left: `${((wm.center - barStart) / barSpan) * 100}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: Math.min(38, barHeight * 0.62),
                  fontWeight: 800,
                  color: 'rgba(0,0,0,0.12)',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                {wm.label}
              </div>
            ))}

            {/* One-time "drag like this" coach mark -- a ghost copy of the
                real drag handle's look, sliding right once and fading out.
                pointerEvents: 'none' so it's purely a visual demo, never a
                hit target; gated on !range so it's gone the instant a real
                selection exists, whether from finishing the demo or the
                guest just going ahead and dragging before it does. */}
            {isMobile && showGestureHint && !range && (
              <motion.div
                initial={{ opacity: 0, x: 0 }}
                animate={{ opacity: [0, 1, 1, 0], x: [0, 0, 64, 64] }}
                transition={{ duration: 1.8, times: [0, 0.15, 0.75, 1], ease: 'easeInOut' }}
                onAnimationComplete={() => setShowGestureHint(false)}
                style={{
                  position: 'absolute',
                  left: '10%',
                  top: '50%',
                  marginTop: -HANDLE_SIZE / 2,
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    width: HANDLE_SIZE,
                    height: HANDLE_SIZE,
                    borderRadius: '50%',
                    background: '#fff',
                    color: TEAL,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 1px 4px rgba(0,0,0,0.15), 0 0 0 2px ${TEAL}`,
                  }}
                >
                  <IconGripVertical size={12} />
                </div>
              </motion.div>
            )}

            {range && (
              <div
                onPointerDown={handleSelectionPointerDown}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  left: `${((range.start - barStart) / barSpan) * 100}%`,
                  width: `${((range.end - range.start) / barSpan) * 100}%`,
                  top: 0,
                  bottom: 0,
                  background: TEAL_SOFT_BG,
                  border: `2px solid ${TEAL}`,
                  borderRadius: 14,
                  // Smooths out the snapMinutes steps into continuous
                  // motion while dragging, instead of the box hopping between
                  // snap points -- purely cosmetic, the committed range is still
                  // exactly what SNAP_MINUTES produces.
                  transition: 'left 180ms ease, width 180ms ease',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'grab',
                  touchAction: 'none',
                }}
              >
                {!isMobile && (
                  <>
                    <Text strong style={{ color: TEAL, fontSize: 13 }}>
                      {formatClock(range.start)} – {formatClock(range.end)}
                    </Text>

                    {/* Same invisible-hit-area pattern as DragHandle above: the
                        visible dot stays 18px, but it's anchored flush to the
                        selection box's top-right corner (rather than the previous
                        4px inset) inside a 44px tappable box, so growing the hit
                        area doesn't push it outside the bar's own `overflow:
                        hidden` bounds. Mobile drops this entirely -- the bigger
                        end handles below already cover resize, and "Elegir otro
                        horario" in the footer covers delete, so a third tiny
                        deselect target has nothing left to do there. */}
                    <div
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        handleReset();
                      }}
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        width: HANDLE_HIT_SIZE,
                        height: HANDLE_HIT_SIZE,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: TEAL,
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 0 0 2px #fff',
                        }}
                      >
                        <IconX size={12} />
                      </div>
                    </div>
                  </>
                )}

                {(['start', 'end'] as const).map((which) => (
                  <DragHandle key={which} side={which} onPointerDown={handleHandlePointerDown(which)} />
                ))}
              </div>
            )}
          </div>

          <div style={{ position: 'relative', height: 16, marginTop: 4 }}>
            {hourMarks.map((mark) => (
              <Text
                key={mark}
                type="secondary"
                style={{
                  position: 'absolute',
                  left: `${((mark - barStart) / barSpan) * 100}%`,
                  transform: 'translateX(-50%)',
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                }}
              >
                {formatClock(mark % MINUTES_PER_DAY)}
              </Text>
            ))}
          </div>
        </div>

        {/* Subtle "there's more this way" hint -- fades out once scrolled to
            the end, reappears if the guest scrolls back away from it, rather
            than a one-time flag that could go stale mid-session. Solid end
            of the gradient matches SURFACE_1_SOFT (the bar's own 'free'
            fill, see segmentStyle above) rather than pure white -- this used
            to blend seamlessly back when the bar's own background WAS
            white, but against the cream fill it left a visibly mismatched
            seam wherever this hint happened to sit. */}
        {isMobile && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 20,
              width: 36,
              background: 'linear-gradient(to right, rgba(252,247,240,0), rgba(252,247,240,0.95))',
              pointerEvents: 'none',
              opacity: scrollAtEnd ? 0 : 1,
              transition: 'opacity 200ms ease',
            }}
          />
        )}
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12, marginBottom: 12 }}>
        <Legend swatch={segmentStyle('buffer').background as string} label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_TIMELINE_LEGEND_BUFFER)} />
        <Legend swatch={segmentStyle('booked').background as string} label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_TIMELINE_LEGEND_BOOKED)} />
        {segments.some((s) => s.kind === 'tooSoon') && (
          <Legend swatch={segmentStyle('tooSoon').background as string} label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_TIMELINE_LEGEND_TOO_SOON)} />
        )}
        <Legend swatch={TEAL} label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_TIMELINE_LEGEND_SELECTION)} />
      </div>

      {range && (
        <div
          style={{
            display: 'flex',
            justifyContent: isMobile ? 'flex-start' : 'space-between',
            alignItems: 'center',
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 12,
          }}
        >
          {!isMobile && (
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DURATION_LABEL)}
              </Text>
              <Text strong style={{ fontSize: 16 }}>
                {durationLabel}
                {price != null && <> — {formatCurrency(price, currencyCode ?? '', i18n.language)}</>}
              </Text>
            </div>
          )}
          <ConfigProvider theme={tealAccentTheme}>
            <Space>
              <motion.div style={{ display: 'inline-block' }} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.15 }}>
                <Button shape="round" onClick={handleReset}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_TIMELINE_RESET_BUTTON)}
                </Button>
              </motion.div>
              {!isMobile && (
                <motion.div style={{ display: 'inline-block' }} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.15 }}>
                  <Button type="primary" shape="round" onClick={() => handleConfirm()}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONTINUE_BUTTON)}
                  </Button>
                </motion.div>
              )}
            </Space>
          </ConfigProvider>
        </div>
      )}
    </motion.div>
  );
};

const Legend: React.FC<{ swatch: string; label: string }> = ({ swatch, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: swatch }} />
    <Text type="secondary" style={{ fontSize: 12 }}>
      {label}
    </Text>
  </div>
);

export default HourlyRangeTimeline;
